import * as React from 'react';
import { addImageParams, AddImageParamTypes, getCategoryPageUrl, imagePlaceholder } from '@/utils/urls';
import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { Category } from '../../types/CategoryTypes';
import { CategoryNamesAndIds } from '../../constants/constant';
import { FormattedNumber, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { RouteButton } from '@/components/RouteButton/RouteButton';
import { urlToSend } from '../utils/urlToSend';
import FormattedMessage from '@/components/Formatted/FormattedMessageText';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styled from 'styled-components';

export type DiscoverCategoriesSubcategoryProps = {
    category: Category[];
    title: string;
};

const SubCategory = (
    { category, title }: DiscoverCategoriesSubcategoryProps,
    ref?: React.MutableRefObject<HTMLDivElement>
) => {
    const { formatMessage } = useIntl();
    return (
        <StyledSubCategoriesWrapper ref={ref}>
            <HeaderWrapper>
                <H2>
                    <FormattedMessage
                        id="discoverPage.subCategory.subCategoryTitle"
                        values={{ title }}
                    />
                </H2>
                <RouteButton
                    size="sm"
                    suppressHydrationWarning
                    to={getCategoryPageUrl(urlToSend(title.toLocaleLowerCase()), `${CategoryNamesAndIds[title]}`)}
                >
                    {formatMessage({ id: 'discoverPage.subCategory.subCategoryLink' }, { title })}
                </RouteButton>
            </HeaderWrapper>
            <SubCategoriesContainer>
                {category.map((subCategory: Category, index: number) => {
                    const imageParams: AddImageParamTypes = {
                        height: 184,
                        url: subCategory.imageUrl || imagePlaceholder,
                    };

                    return (
                        <StyledLink
                            key={index}
                            route
                            to={subCategory.pageUrl}
                        >
                            <StyledSubCategory>
                                <ImageWrapper>
                                    <img
                                        alt={subCategory.title}
                                        loading={index < 8 ? 'eager' : 'lazy'}
                                        src={addImageParams(imageParams)}
                                    />
                                </ImageWrapper>
                                <StyledSubCategoryContent>
                                    <BodyPrimary>
                                        <FormattedMessage
                                            id="discoverPage.subCategory.subCategoryTitleSpan"
                                            values={{
                                                title: subCategory.title,
                                            }}
                                        />
                                    </BodyPrimary>
                                    {subCategory.followers > 0 && (
                                        <BodyPrimary color="secondary">
                                            {subCategory.followers === 1 ? (
                                                <FormattedMessage id="discoverPage.subCategory.subCategoryFollower" />
                                            ) : (
                                                <FormattedMessage
                                                    id="discoverPage.subCategory.subCategoryFollowers"
                                                    values={{
                                                        followers: (
                                                            <FormattedNumber
                                                                key="subcategory_followers"
                                                                value={subCategory.followers}
                                                            />
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </BodyPrimary>
                                    )}
                                </StyledSubCategoryContent>
                            </StyledSubCategory>
                        </StyledLink>
                    );
                })}
            </SubCategoriesContainer>
        </StyledSubCategoriesWrapper>
    );
};

export default React.forwardRef(SubCategory);

const StyledSubCategoriesWrapper = styled.div`
    margin-top: 55px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-left: 10px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-left: 66px;
    }
`;

const HeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 40px;
    width: 89%;

    h2 {
        margin-left: 32px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        width: 88%;
        h2 {
            margin-left: 0;
        }
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 84.7%;
    }
`;

const SubCategoriesContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-left: 32px;
    padding-top: 24px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-left: 0;
    }
`;

const StyledSubCategory = styled.div`
    align-content: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 20px 0;
    text-align: left;

    img {
        border-radius: 4px;
        max-height: 100%;
        max-width: 100%;
    }
    &:hover {
        cursor: pointer;

        span {
            color: ${({ theme }) => theme.colors.blue200};
            text-decoration: underline;
        }
    }
`;

const ImageWrapper = styled.div`
    align-items: center;
    border-radius: 5px;
    display: flex;
    height: 184px;
    justify-content: center;
    margin-bottom: 5px;
    position: relative;
    width: 184px;
`;

const StyledLink = styled(Link)`
    margin-right: 19.5px;
    text-decoration: none;
    max-width: 184px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-right: 25px;
        width: 178px;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-right: 26px;
    }
`;

const StyledSubCategoryContent = styled.div`
    display: flex;
    flex-direction: column;

    &:hover {
        span {
            color: ${({ theme }) => theme.colors.blue200};
            text-decoration: underline;
        }
    }
`;
